"use client";

import Category from "@/components/atoms/product-page/category/Category";
import { Locales } from "@/lib/i18n";
import { __, getPermalink } from "@/lib/i18n/functions";
import { CategorieDocument, InspirationDocument } from "@/prismicio-types";
import { isFilled } from "@prismicio/client";
import React, { Fragment, useState } from "react";

type CategoriesAndThematiquesTabProps = {
  categories: CategorieDocument[];
  thematiques: InspirationDocument[];
  locale: Locales;
};

const CategoriesAndThematiquesTab = ({ categories, thematiques, locale }: CategoriesAndThematiquesTabProps) => {
  const [activeTab, setActiveTab] = useState(0);

  const tabs = [
    {
      title: __("Our categories", locale),
      cards: categories,
    },
    {
      title: __("Our inspirations", locale),
      cards: thematiques,
    },
  ];

  const getImageUrl = (doc: InspirationDocument | CategorieDocument) => {
    if ("featured_image" in doc.data) {
      return doc.data.featured_image?.url || "";
    }
    return doc.data.featuredImage?.url || "";
  };

  const getImageAlt = (doc: InspirationDocument | CategorieDocument) => {
    if ("featured_image" in doc.data) {
      return doc.data.featured_image?.alt || doc.data.title || "";
    }
    return doc.data.featuredImage?.alt || doc.data.title || "";
  };

  const resolveLink = (doc: InspirationDocument | CategorieDocument) => {
    if (doc.type === "categorie") {
      return `${getPermalink(locale, "categories")}/${doc.uid}`;
    }
    if (doc.type === "inspiration") {
      const validCategory = doc.data.categories.find(
        (cat) => isFilled.contentRelationship(cat.inspirationCategory) && "slug" in cat.inspirationCategory
      );

      if (!validCategory || !("slug" in validCategory.inspirationCategory)) return "/";

      const slug = validCategory.inspirationCategory.slug;

      return `${getPermalink(locale, "inspiration_archive")}/${slug}#${doc.uid}`;
    }
    if ((doc as any).type === "inspiration_category") {
      return `${getPermalink(locale, "inspiration_archive")}/${(doc as any).uid}`;
    }
    return "/";
  };

  return (
    <>
      {/* Tab Navigation */}
      <nav className="mb-10 flex justify-center" role="tablist" aria-label="Category Tabs">
        <div className="relative flex items-start space-x-5">
          {tabs.map((tab, index) => (
            <Fragment key={tab.title}>
              <button
                id={`tab-${index}`}
                role="tab"
                aria-selected={activeTab === index}
                aria-controls={`tabpanel-${index}`}
                onClick={() => setActiveTab(index)}
                className={`font-cabinet-grotesk relative cursor-pointer pb-4 text-xl font-bold transition-colors md:text-2xl ${
                  activeTab === index ? "text-primary" : "text-[#9c9c9c]"
                }`}
              >
                {tab.title}
                {activeTab === index && <span className="absolute bottom-0 left-0 h-0.5 w-full bg-[#000000]" />}
              </button>

              {index < tabs.length - 1 && (
                <div aria-hidden="true" className="mb-5 h-3/4 w-0.5 rounded-sm bg-[#d9d9d9]" />
              )}
            </Fragment>
          ))}
        </div>
      </nav>

      {/* Cards Grid */}
      <section
        id={`tabpanel-${activeTab}`}
        role="tabpanel"
        aria-labelledby={`tab-${activeTab}`}
        tabIndex={0}
        className="grid justify-items-center gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4"
      >
        {tabs[activeTab]?.cards?.map((doc, idx) => (
          <Category
            key={idx}
            text={doc.data.title || ""}
            imageSRC={getImageUrl(doc)}
            imageALT={getImageAlt(doc)}
            link={resolveLink(doc)}
            {...doc}
            textPosition="center"
            locale={locale}
            priority={idx === 0}
          />
        ))}
      </section>
    </>
  );
};

export default CategoriesAndThematiquesTab;
